// Arena grid geometry shared by the sim, the protocol and the renderer (spec §1).
//
// - Cells: `(cx, cy) = (floor(x), floor(y))`, `x ∈ [0, cols)`, `y ∈ [0, rows)`, +y down. A cell's
//   index is `cy · cols + cx` (row-major), the index of the sim's `owner` / `trailOwner` arrays
//   and of every cell on the wire.
// - Chunks (ARENA_NET.chunkSize = 16): chunk `(kx, ky)` holds cells `[16kx, 16kx+16) ×
//   [16ky, 16ky+16)` clipped to the grid; chunk index `ky · chunksX + kx`; cells inside a chunk
//   are row-major over the full 16 × 16 (cells past the grid edge count as neutral and are sent
//   as part of the last run).
// - The playable mask (circle or square) MUST be computed by `isPlayableCell` on both sides, so
//   the server's playable-cell count and the client's drawing agree.
//
// Index maths: contracts step. The mask (`isPlayableCell`, `playableCellCount`, `arenaCircle`):
// lane S (the exact circle rule decides the 83,000 ± 0.5 % acceptance of backlog S-2).

import { ARENA_NET, type ArenaWorldShape } from './constants.js';

/** What the client learns from WELCOME and the sim from config: enough to rebuild the mask. */
export interface ArenaGridShape {
  readonly shape: ArenaWorldShape;
  readonly cols: number;
  readonly rows: number;
}

export function cellIndex(cx: number, cy: number, cols: number): number {
  return cy * cols + cx;
}

export function cellX(index: number, cols: number): number {
  return index % cols;
}

export function cellY(index: number, cols: number): number {
  return Math.floor(index / cols);
}

/** Chunks across / down for a grid side (ceil(side / 16)). */
export function chunksAlong(cells: number): number {
  return Math.ceil(cells / ARENA_NET.chunkSize);
}

/** The chunk index holding cell `(cx, cy)`. */
export function chunkIndexOfCell(cx: number, cy: number, cols: number): number {
  const s = ARENA_NET.chunkSize;
  return Math.floor(cy / s) * chunksAlong(cols) + Math.floor(cx / s);
}

/**
 * The circle mask (spec §1, territory.md §2): centre `(cols / 2, rows / 2)`, radius
 * `(min(cols, rows) − 1) / 2` (Ø 325 in a 326 × 326 grid), so the outermost ring of grid cells is
 * always wall and the fill always has a seed.
 */
export interface ArenaCircle {
  readonly cx: number;
  readonly cy: number;
  /** A cell is playable when its centre lies within this distance of the centre. */
  readonly r: number;
  /**
   * The head's position stays within this distance of the centre (`r − √½`): every point that
   * close lies in a playable cell, so a head never stands in the wall (backlog S-2).
   */
  readonly headR: number;
}

export function arenaCircle(g: ArenaGridShape): ArenaCircle {
  const r = (Math.min(g.cols, g.rows) - 1) / 2;
  return { cx: g.cols / 2, cy: g.rows / 2, r, headR: r - Math.SQRT1_2 };
}

/**
 * Square worlds: the head's position stays in `[SQUARE_HEAD_MARGIN, side − SQUARE_HEAD_MARGIN]`
 * (the drawn head never pokes out of the grid; every cell is still reachable).
 */
export const SQUARE_HEAD_MARGIN = 0.5;

/**
 * True if cell `(cx, cy)` is inside the arena (not wall). Square: every in-grid cell. Circle: the
 * cell's centre `(cx + ½, cy + ½)` lies within `arenaCircle(g).r` of the grid centre (Ø 325 in a
 * 326 × 326 grid ≈ 82,950 cells, backlog S-2). Out-of-grid cells are never playable.
 */
export function isPlayableCell(g: ArenaGridShape, cx: number, cy: number): boolean {
  if (cx < 0 || cy < 0 || cx >= g.cols || cy >= g.rows) return false;
  if (g.shape === 'square') return true;
  const c = arenaCircle(g);
  const dx = cx + 0.5 - c.cx;
  const dy = cy + 0.5 - c.cy;
  return dx * dx + dy * dy <= c.r * c.r;
}

/**
 * True if a head can stand in cell `(cx, cy)`: it is playable and (circle) the cell's point
 * nearest the centre lies within `arenaCircle(g).headR`. Every square cell is reachable. A few
 * circle rim cells are playable but not reachable (their centre is within `r`, the whole cell
 * beyond `headR`): land and capture count them, but heads, the autopilot and drops never use
 * them.
 */
export function isReachableCell(g: ArenaGridShape, cx: number, cy: number): boolean {
  if (!isPlayableCell(g, cx, cy)) return false;
  if (g.shape === 'square') return true;
  const c = arenaCircle(g);
  const nx = Math.max(cx, Math.min(c.cx, cx + 1)) - c.cx;
  const ny = Math.max(cy, Math.min(c.cy, cy + 1)) - c.cy;
  return nx * nx + ny * ny <= c.headR * c.headR;
}

/** Number of playable cells (the land-percent denominator, WELCOME `playableCells`). */
export function playableCellCount(g: ArenaGridShape): number {
  if (g.shape === 'square') return g.cols * g.rows;
  let n = 0;
  for (let cy = 0; cy < g.rows; cy++) {
    for (let cx = 0; cx < g.cols; cx++) if (isPlayableCell(g, cx, cy)) n++;
  }
  return n;
}
